/**
 * Builds normalized exiting/entering fund pairs for plan recommendation sheets.
 *
 * Belongs here: header identity ↔ tab mapping; per-leg ₹ only from counter/tab funds.
 * Does not belong here: sheet layout, tax outside-vs-inside placement, or API fetching.
 */
import type {
  LumpsumAllocationBuyFundDetail,
  LumpsumAllocationBuyPlanRecommendationDetails,
  LumpsumAllocationSellFundDetail,
  LumpsumAllocationSellPlanRecommendationDetails
} from '../api/allocationRecommendation.types'
import type {
  LumpsumRebalanceBuyPlanRecommendationDetails,
  LumpsumRebalanceSellPlanRecommendationDetails
} from '../api/rebalanceRecommendation.types'
import type { LumpsumPlanRecommendationCounterFund } from '../api/recommendation.types'
import { showAsFormattedRupeeAmount } from '../../../display-values/lumpsumPlanAmount'
import {
  PlanRecommendationTabFundSide,
  type PlanRecommendationPairFundDetails,
  type PlanRecommendationPairView
} from './planRecommendationPair.types'

/** Round like fund cards so accordion / tax amounts match displayed ₹. */
function roundedAmount(rawAmount: number | null | undefined): number {
  return showAsFormattedRupeeAmount({ rawAmount: rawAmount ?? 0 }).value
}

/**
 * Buy-entry sheet: header is the buy fund; each tab is a sell fund funding it.
 * Per-leg amounts both come from the sell tab (sellAmount + buyAmount).
 * Called from AllocationBuyPlanRecommendationBody per selected sell tab.
 */
export function buildAllocationBuyRecommendationPair(
  planDetails: LumpsumAllocationBuyPlanRecommendationDetails | null,
  sellTabFund: LumpsumAllocationSellFundDetail | null | undefined
): PlanRecommendationPairView | null {
  if (!planDetails || !sellTabFund) {
    return null
  }

  return {
    planSnapshotDate: planDetails?.planSnapshotDate ?? null,
    tabFundSide: PlanRecommendationTabFundSide.EXITING,
    // Sell tab carries its own sellAmount for redeem / in-tab tax.
    exitingFund: mapAllocationSellTabToExitingFundDetails(sellTabFund),
    enteringFund: {
      ...mapAllocationBuyHeaderToEnteringFundDetails(planDetails),
      // Per-leg buy ₹ from the sell tab (header has identity only — no amount).
      buyAmount: roundedAmount(sellTabFund?.buyAmount)
    },
    sellReasons: sellTabFund?.sellReasons ?? null,
    buyReasons: sellTabFund?.buyReasons ?? null,
    underPerformance: null,
    highExposure: null,
    overExposure: null,
    overDiversification: null
  }
}

/**
 * Sell-entry sheet: header is the sell fund; each tab is a buy fund receiving part of the sale.
 * Per-leg amounts both come from the buy tab (sellAmount + buyAmount).
 * Called from AllocationSellPlanRecommendationBody per selected buy tab.
 */
export function buildAllocationSellRecommendationPair(
  planDetails: LumpsumAllocationSellPlanRecommendationDetails | null,
  buyTabFund: LumpsumAllocationBuyFundDetail | null | undefined
): PlanRecommendationPairView | null {
  if (!planDetails || !buyTabFund) {
    return null
  }

  return {
    planSnapshotDate: planDetails?.planSnapshotDate ?? null,
    tabFundSide: PlanRecommendationTabFundSide.ENTERING,
    exitingFund: {
      ...mapAllocationSellHeaderToExitingFundDetails(planDetails),
      // Per-leg sell ₹ from the buy tab (header has identity only — no amount).
      sellAmount: roundedAmount(buyTabFund?.sellAmount)
    },
    // Buy tab carries its own buyAmount for the entering fund card.
    enteringFund: mapAllocationBuyTabToEnteringFundDetails(buyTabFund),
    sellReasons: buyTabFund?.sellReasons ?? null,
    buyReasons: buyTabFund?.buyReasons ?? null,
    underPerformance: null,
    highExposure: null,
    overExposure: null,
    overDiversification: null
  }
}

/**
 * Rebalance buy-entry — same layout as allocation buy.
 * Per-leg amounts both come from the sell tab (sellAmount + buyAmount).
 */
export function buildRebalanceBuyRecommendationPair(
  planDetails: LumpsumRebalanceBuyPlanRecommendationDetails | null,
  sellTabFund: LumpsumPlanRecommendationCounterFund | null | undefined
): PlanRecommendationPairView | null {
  if (!planDetails || !sellTabFund) {
    return null
  }

  return {
    planSnapshotDate: planDetails?.planSnapshotDate ?? null,
    tabFundSide: PlanRecommendationTabFundSide.EXITING,
    exitingFund: mapRebalanceSellTabToExitingFundDetails(sellTabFund),
    enteringFund: {
      ...mapRebalanceBuyHeaderToEnteringFundDetails(planDetails),
      // Per-leg buy ₹ from the sell tab (header has identity only — no amount).
      buyAmount: roundedAmount(sellTabFund?.buyAmount)
    },
    sellReasons: null,
    buyReasons: null,
    underPerformance: sellTabFund?.underPerformance ?? null,
    highExposure: sellTabFund?.highExposure ?? null,
    overExposure: null,
    overDiversification: null
  }
}

/**
 * Rebalance sell-entry — same layout as allocation sell.
 * Per-leg amounts both come from the buy tab (sellAmount + buyAmount).
 */
export function buildRebalanceSellRecommendationPair(
  planDetails: LumpsumRebalanceSellPlanRecommendationDetails | null,
  buyTabFund: LumpsumPlanRecommendationCounterFund | null | undefined
): PlanRecommendationPairView | null {
  if (!planDetails || !buyTabFund) {
    return null
  }

  return {
    planSnapshotDate: planDetails?.planSnapshotDate ?? null,
    tabFundSide: PlanRecommendationTabFundSide.ENTERING,
    exitingFund: {
      ...mapRebalanceSellHeaderToExitingFundDetails(planDetails),
      // Per-leg sell ₹ from the buy tab (header has identity only — no amount).
      sellAmount: roundedAmount(buyTabFund?.sellAmount)
    },
    enteringFund: mapRebalanceBuyTabToEnteringFundDetails(buyTabFund),
    sellReasons: null,
    buyReasons: null,
    underPerformance: buyTabFund?.underPerformance ?? null,
    highExposure: buyTabFund?.highExposure ?? null,
    overExposure: null,
    overDiversification: null
  }
}

/** Sell-tab → exiting fund; sellAmount is this counter fund’s redeem leg. */
function mapAllocationSellTabToExitingFundDetails(
  sellTabFund: LumpsumAllocationSellFundDetail
): PlanRecommendationPairFundDetails {
  return {
    fundName: sellTabFund?.fundName ?? '',
    isin: sellTabFund?.isin ?? '',
    categoryName: sellTabFund?.categoryName ?? '',
    powerRank: sellTabFund?.powerRank ?? null,
    powerRating: sellTabFund?.powerRating ?? null,
    mfLogoUrl: sellTabFund?.mfLogoUrl ?? '',
    currentValue: sellTabFund?.taxData?.currentValue ?? 0,
    sellAmount: roundedAmount(sellTabFund?.sellAmount),
    transferType: sellTabFund?.transferType ?? null,
    swpAmount: sellTabFund?.swpAmount ?? null,
    swpDurationInMonths: sellTabFund?.swpDurationInMonths ?? null,
    executed: sellTabFund?.executed ?? false,
    taxData: sellTabFund?.taxData ?? null,
    categoryAllocationExecutionStatus: sellTabFund?.categoryAllocationExecutionStatus ?? null,
    categoryFundCountExecutionStatus: sellTabFund?.categoryFundCountExecutionStatus ?? null
  }
}

/**
 * Header identity for the buy fund on a buy-entry sheet.
 * No buyAmount here — pair amounts come only from the active sell tab’s legs.
 * (Sheet root totalTransferAmount stays on intro/header/tax, not on the pair.)
 */
function mapAllocationBuyHeaderToEnteringFundDetails(
  planDetails: LumpsumAllocationBuyPlanRecommendationDetails
): PlanRecommendationPairFundDetails {
  return {
    fundName: planDetails?.fundName ?? '',
    isin: planDetails?.isin ?? '',
    categoryName: planDetails?.categoryName ?? '',
    powerRank: planDetails?.powerRank ?? null,
    powerRating: planDetails?.powerRating ?? null,
    mfLogoUrl: planDetails?.mfLogoUrl ?? '',
    currentValue: planDetails?.currentValue ?? 0,
    transferType: null,
    swpAmount: null,
    swpDurationInMonths: null,
    executed: planDetails?.executed ?? false,
    taxData: planDetails?.consolidatedTax ?? null,
    categoryAllocationExecutionStatus: planDetails?.categoryAllocationExecutionStatus ?? null,
    categoryFundCountExecutionStatus: planDetails?.categoryFundCountExecutionStatus ?? null
  }
}

/**
 * Header identity for the sell fund on a sell-entry sheet.
 * No sellAmount here — pair amounts come only from the active buy tab’s legs.
 * (Sheet root totalTransferAmount stays on intro/header/tax, not on the pair.)
 */
function mapAllocationSellHeaderToExitingFundDetails(
  planDetails: LumpsumAllocationSellPlanRecommendationDetails
): PlanRecommendationPairFundDetails {
  return {
    fundName: planDetails?.fundName ?? '',
    isin: planDetails?.isin ?? '',
    categoryName: planDetails?.categoryName ?? '',
    powerRank: planDetails?.powerRank ?? null,
    powerRating: planDetails?.powerRating ?? null,
    mfLogoUrl: planDetails?.mfLogoUrl ?? '',
    currentValue: planDetails?.currentValue ?? 0,
    transferType: null,
    swpAmount: null,
    swpDurationInMonths: null,
    executed: planDetails?.executed ?? false,
    taxData: planDetails?.consolidatedTax ?? null,
    categoryAllocationExecutionStatus: planDetails?.categoryAllocationExecutionStatus ?? null,
    categoryFundCountExecutionStatus: planDetails?.categoryFundCountExecutionStatus ?? null
  }
}

/** Buy-tab → entering fund; buyAmount is this counter fund’s buy leg. */
function mapAllocationBuyTabToEnteringFundDetails(
  buyTabFund: LumpsumAllocationBuyFundDetail
): PlanRecommendationPairFundDetails {
  return {
    fundName: buyTabFund?.fundName ?? '',
    isin: buyTabFund?.isin ?? '',
    categoryName: buyTabFund?.categoryName ?? '',
    powerRank: buyTabFund?.powerRank ?? null,
    powerRating: buyTabFund?.powerRating ?? null,
    mfLogoUrl: buyTabFund?.mfLogoUrl ?? '',
    currentValue: buyTabFund?.taxData?.currentValue ?? 0,
    buyAmount: roundedAmount(buyTabFund?.buyAmount),
    transferType: buyTabFund?.transferType ?? null,
    swpAmount: buyTabFund?.swpAmount ?? null,
    swpDurationInMonths: buyTabFund?.swpDurationInMonths ?? null,
    executed: buyTabFund?.executed ?? false,
    taxData: buyTabFund?.taxData ?? null,
    categoryAllocationExecutionStatus: buyTabFund?.categoryAllocationExecutionStatus ?? null,
    categoryFundCountExecutionStatus: buyTabFund?.categoryFundCountExecutionStatus ?? null
  }
}

/** Rebalance sell-tab → exiting fund; sellAmount is this counter fund’s redeem leg. */
function mapRebalanceSellTabToExitingFundDetails(
  sellTabFund: LumpsumPlanRecommendationCounterFund
): PlanRecommendationPairFundDetails {
  return {
    fundName: sellTabFund?.fundName ?? '',
    isin: sellTabFund?.isin ?? '',
    categoryName: sellTabFund?.fundCategory ?? '',
    powerRank: sellTabFund?.powerRank ?? null,
    powerRating: sellTabFund?.powerRating ?? null,
    mfLogoUrl: sellTabFund?.mfLogoUrl ?? '',
    currentValue: sellTabFund?.taxData?.currentValue ?? 0,
    sellAmount: roundedAmount(sellTabFund?.sellAmount),
    transferType: sellTabFund?.transferType ?? null,
    swpAmount: sellTabFund?.swpAmount ?? null,
    swpDurationInMonths: sellTabFund?.swpDurationInMonths ?? null,
    executed: sellTabFund?.executed ?? false,
    taxData: sellTabFund?.taxData ?? null,
    categoryAllocationExecutionStatus: null,
    categoryFundCountExecutionStatus: null
  }
}

/**
 * Header identity for the buy fund on a rebalance buy-entry sheet.
 * No buyAmount here — pair amounts come only from the active sell tab’s legs.
 */
function mapRebalanceBuyHeaderToEnteringFundDetails(
  planDetails: LumpsumRebalanceBuyPlanRecommendationDetails
): PlanRecommendationPairFundDetails {
  return {
    fundName: planDetails?.fundName ?? '',
    isin: planDetails?.isin ?? '',
    categoryName: planDetails?.fundCategory ?? '',
    powerRank: planDetails?.powerRank ?? null,
    powerRating: planDetails?.powerRating ?? null,
    mfLogoUrl: planDetails?.mfLogoUrl ?? '',
    currentValue: planDetails?.currentValue ?? 0,
    transferType: null,
    swpAmount: null,
    swpDurationInMonths: null,
    executed: planDetails?.executed ?? false,
    taxData: planDetails?.consolidatedTax ?? null,
    categoryAllocationExecutionStatus: null,
    categoryFundCountExecutionStatus: null
  }
}

/**
 * Header identity for the sell fund on a rebalance sell-entry sheet.
 * No sellAmount here — pair amounts come only from the active buy tab’s legs.
 */
function mapRebalanceSellHeaderToExitingFundDetails(
  planDetails: LumpsumRebalanceSellPlanRecommendationDetails
): PlanRecommendationPairFundDetails {
  return {
    fundName: planDetails?.fundName ?? '',
    isin: planDetails?.isin ?? '',
    categoryName: planDetails?.fundCategory ?? '',
    powerRank: planDetails?.powerRank ?? null,
    powerRating: planDetails?.powerRating ?? null,
    mfLogoUrl: planDetails?.mfLogoUrl ?? '',
    currentValue: planDetails?.currentValue ?? 0,
    transferType: null,
    swpAmount: null,
    swpDurationInMonths: null,
    executed: planDetails?.executed ?? false,
    taxData: planDetails?.consolidatedTax ?? null,
    categoryAllocationExecutionStatus: null,
    categoryFundCountExecutionStatus: null
  }
}

/** Rebalance buy-tab → entering fund; buyAmount is this counter fund’s buy leg. */
function mapRebalanceBuyTabToEnteringFundDetails(
  buyTabFund: LumpsumPlanRecommendationCounterFund
): PlanRecommendationPairFundDetails {
  return {
    fundName: buyTabFund?.fundName ?? '',
    isin: buyTabFund?.isin ?? '',
    categoryName: buyTabFund?.fundCategory ?? '',
    powerRank: buyTabFund?.powerRank ?? null,
    powerRating: buyTabFund?.powerRating ?? null,
    mfLogoUrl: buyTabFund?.mfLogoUrl ?? '',
    currentValue: buyTabFund?.taxData?.currentValue ?? 0,
    buyAmount: roundedAmount(buyTabFund?.buyAmount),
    transferType: buyTabFund?.transferType ?? null,
    swpAmount: buyTabFund?.swpAmount ?? null,
    swpDurationInMonths: buyTabFund?.swpDurationInMonths ?? null,
    executed: buyTabFund?.executed ?? false,
    taxData: buyTabFund?.taxData ?? null,
    categoryAllocationExecutionStatus: null,
    categoryFundCountExecutionStatus: null
  }
}
